import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { UserSettingsApi } from '../../api/UserSettingsApi';
import { myConfigurationData } from '../../store/Helpers/ProductSettingsSliceHelper';
import { UserApi } from '../../api/UserApi';

import type { IAuthorizationRequest, IMyConfigurationRequest } from '../../../types/IApiTypes';
import type { IFilterNodeWithOrder, IUser } from '../../../types/ITypes';
import { StateDisplayOrder } from '../../data/Constants';

const initialState: IUser = {
  details: {},
  myConfigurations: {},
  myConfigPageDetails: { page: 1, limit: 15, totalRecords: 1 },
  category: { channels: [], applications: [], states: [] },
  loading: false,
};

//Gets User logged in details
export const getUserDetails = createAsyncThunk(
  'user/getUserDetails',
  async (action: IAuthorizationRequest) => {
    return UserSettingsApi.getUserDetails(action);
  }
);

//Gets all the user specific configurations
export const getMyConfigurations = createAsyncThunk(
  'user/getMyConfigurations',
  async (action: IMyConfigurationRequest) => {
    return UserApi.myConfigurations(action);
  }
);

//Gets the channels that are available
export const getCategories = createAsyncThunk(
  'user/getCategories',
  async (action: IAuthorizationRequest) => {
    return UserApi.getCategories(action);
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    updateMyConfigPageDetails(state, action) {
      state.myConfigPageDetails = {
        page: action.payload.page,
        limit: action.payload.limit,
        totalRecords: action.payload.totalRecords,
        channelCodes: action.payload.channelCodes,
        applicationIds: action.payload.applicationIds,
        states: action.payload.states,
      };
    },
    resetMyConfigurations(state) {
      state.myConfigurations = {};
      state.category = { channels: [], applications: [], states: [] };
      state.myConfigPageDetails = { page: 1, limit: 15, totalRecords: 1 };
    },
    // Add new reducer to handle configuration deletion
    removeConfigurationById(state, action) {
      const configId = action.payload;
      // Update configurations in the current page
      const currentPage = state.myConfigPageDetails.page;
      if (state.myConfigurations[currentPage]) {
        state.myConfigurations[currentPage] = state.myConfigurations[currentPage].filter(
          (config) => config.configurationId !== configId
        );
      }
      // Update total records count
      if (state?.myConfigPageDetails?.totalRecords) {
        state.myConfigPageDetails.totalRecords -= 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserDetails.fulfilled, (state, action) => {
        if (action.payload.data) {
          state.details = action.payload.data;
        }
      })
      .addCase(getMyConfigurations.fulfilled, (state, action) => {
        if (action.payload) {
          const configurationData = myConfigurationData(action.payload, state);
          state.myConfigurations = configurationData.myConfigurations;
          state.myConfigPageDetails = {
            ...state.myConfigPageDetails,
            ...configurationData.myConfigPageDetails,
          };
        }
      })
      .addCase(getCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCategories.fulfilled, (state, action) => {
        if (action.payload) {
          state.loading = false;
          action.payload.category.states.map((item: IFilterNodeWithOrder) => {
            item.order = StateDisplayOrder[item.name as keyof typeof StateDisplayOrder] ?? 25; // Assign a default order if not provided
          });
          action.payload.category.states.sort(
            (a: IFilterNodeWithOrder, b: IFilterNodeWithOrder) => a.order - b.order
          );
          state.category = action.payload.category;
        }
      });
  },
});

export const { updateMyConfigPageDetails, resetMyConfigurations, removeConfigurationById } =
  userSlice.actions;

export default userSlice.reducer;
